import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();

export const CelebrityCard = ({ celeb, selected, dimmed, onClick, size = "lg", testId }) => {
  const [c1, c2, accent] = celeb.palette;
  const aspect = size === "sm" ? "aspect-square" : "aspect-[3/4]";

  return (
    <motion.button
      layout
      whileTap={{ scale: 0.94 }}
      whileHover={{ y: -3 }}
      animate={{
        opacity: dimmed ? 0.35 : 1,
        filter: dimmed ? "saturate(0.5)" : "saturate(1)",
        scale: selected ? 1.02 : 1,
      }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      onClick={onClick}
      data-testid={testId}
      className={`group relative overflow-hidden rounded-3xl border-2 w-full text-left ${aspect} ${
        selected
          ? "border-[#FF107A] glow-pink"
          : "border-white/10 hover:border-white/30"
      }`}
      style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
    >
      {/* Halftone pattern */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(${accent} 1.4px, transparent 1.4px)`,
          backgroundSize: "10px 10px",
        }}
      />
      {/* Diagonal shine */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)" }} />

      {/* Big initials watermark */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="font-display leading-none opacity-40"
          style={{
            color: accent,
            textShadow: `0 0 32px ${accent}66`,
            fontSize: "clamp(6rem, 22vw, 11rem)",
          }}
        >
          {initials(celeb.name)}
        </span>
      </div>

      {/* Category chip */}
      <div className="absolute top-3 left-3">
        <span className="text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 rounded-full backdrop-blur-md bg-black/50 text-[#FDFBF7] border border-white/20">
          {celeb.category}
        </span>
      </div>
      {/* Era */}
      <div className="absolute top-3 right-3">
        <span className="text-[10px] tracking-[0.15em] uppercase font-semibold px-2 py-1 rounded-full bg-black/50 backdrop-blur-md text-[#FDFBF7]/80 border border-white/10">
          {celeb.era}
        </span>
      </div>

      {/* Bottom overlay */}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/95 via-black/70 to-transparent">
        <div className="font-display text-3xl sm:text-4xl leading-[0.9] tracking-tight text-[#FDFBF7]">
          {celeb.name}
        </div>
        <p className="mt-1.5 text-[11px] sm:text-xs text-[#FDFBF7]/70 line-clamp-2 leading-snug">
          {celeb.descriptor}
        </p>
      </div>

      {/* Selected sticker */}
      {selected && (
        <motion.div
          initial={{ scale: 0.6, opacity: 0, rotate: -20 }}
          animate={{ scale: 1, opacity: 1, rotate: -8 }}
          transition={{ type: "spring", stiffness: 380, damping: 16 }}
          className="absolute top-4 right-4 pointer-events-none"
        >
          <div className="flex items-center gap-1 font-display text-sm tracking-widest bg-[#FF107A] text-[#FDFBF7] px-2.5 py-1 rounded-md border-2 border-[#FDFBF7] glow-pink">
            <Check size={12} strokeWidth={3} /> PICKED
          </div>
        </motion.div>
      )}
    </motion.button>
  );
};
